import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { processSteps } from "@/lib/data";
import { ArrowUpRight } from "lucide-react";

const checklist = [
    "Displays and mounting locations",
    "Wi-Fi coverage",
    "Networking equipment",
    "Audio zones",
    "POS stations",
    "Printers and workstations",
    "Menu boards and signage",
    "Cable pathways",
    "Testing and staff handover",
];

export function NewLocation() {
    return (
        <section data-testid="new-location" className="border-t border-line">
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-32 grid md:grid-cols-12 gap-10">
                <div className="md:col-span-6">
                    <p className="eyebrow mb-6">Openings, renovations & tenant improvements</p>
                    <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[0.95] mb-6">Sort the technology before opening day.</h2>
                    <p className="text-muted leading-relaxed max-w-xl mb-8">
                        We plan the technology piece alongside your contractor, designer and equipment suppliers — from cable pathways in the drywall stage to a full staff handover the week you open. Fewer surprises, fewer last-minute calls.
                    </p>
                    <Link to="/contact" className="inline-flex items-center gap-2 bg-ink text-bg px-6 py-4 text-sm hover:bg-accent transition-colors duration-300">Plan Your Technology Setup <ArrowUpRight className="w-4 h-4" /></Link>
                </div>
                <div className="md:col-span-6">
                    <p className="eyebrow mb-6">Project checklist</p>
                    <ul className="border-t border-line">
                        {checklist.map((c, i) => (
                            <motion.li key={c}
                                initial={{ opacity: 0, x: -12 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                transition={{ duration: 0.4, delay: i * 0.04 }}
                                className="flex items-center gap-4 py-4 border-b border-line group"
                            >
                                <span className="font-mono text-xs text-muted tracking-widest w-8">0{i + 1}</span>
                                <span className="w-1.5 h-1.5 bg-gold" />
                                <span className="font-display text-lg md:text-xl tracking-tight group-hover:text-gold transition-colors duration-300">{c}</span>
                            </motion.li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    );
}

export function Process() {
    return (
        <section data-testid="process" className="border-t border-line bg-bg-alt">
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-32">
                <div className="mb-14">
                    <p className="eyebrow mb-6">How we run a project</p>
                    <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[0.95] max-w-2xl">Five steps, start to handover.</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-5 border-t border-l border-line">
                    {processSteps.map((step, i) => (
                        <motion.div
                            key={step.n}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.5, delay: i * 0.08 }}
                            className="border-r border-b border-line p-8 min-h-[240px] flex flex-col justify-between hover:bg-bg transition-colors duration-300"
                        >
                            <span className="font-mono text-xs tracking-widest text-muted">{step.n}</span>
                            <div>
                                <h3 className="font-display text-xl md:text-2xl tracking-tight mb-3">{step.title}</h3>
                                <p className="text-sm text-muted leading-relaxed">{step.copy}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
